/**
 * Segment traits for the logged-in viewer, and the teams the shell reports as
 * Segment groups.
 *
 * Ported from `frontends/app/src/util/analytics.ts`, which the wandb remote has
 * owned until now. Identity is shell-owned once remotes run under the shell:
 * no remote can be trusted to identify the viewer on another remote's behalf,
 * and a remote that could reassign the user id would clobber every other
 * remote's attribution.
 *
 * The server-deployment branches from the original are dropped — Forge is not a
 * W&B Server deployment, so there is no `deploymentId` standing in for an
 * organization and no reason to suppress `createdAt` and `bio`.
 */

import type {ViewerAnalytics_UserFragment} from '../generated/graphql';

/**
 * Onboarding survey answers, off the untyped `userInfo` JSON column. Gorilla
 * types the field as `JSON`, so codegen gives us `unknown` and this is the
 * shape we assert. Multi-select answers arrive as objects keyed by choice.
 */
type OnboardingChoices = {[key: string]: boolean | undefined};

type ViewerUserInfo = {
  bio?: string;
  websiteUrl?: string;
  howOften?: string;
  howOftenDetails?: string;
  trainReasons?: OnboardingChoices;
  trainReasonsDetails?: string;
  frameworks?: OnboardingChoices;
  frameworksDetails?: string;
  broughtBy?: OnboardingChoices;
  broughtByDetails?: string;
  teamSize?: string;
  teamSizeDetails?: string;
  learningFrom?: OnboardingChoices;
  learningFromDetails?: string;
  whichTools?: OnboardingChoices;
  whichToolsOther?: string;
  mlUseCases?: OnboardingChoices;
  mlUseCasesOther?: string;
  wandbDiscoveryOpts?: OnboardingChoices;
  wandbDiscoveryOptsOther?: string;
};

type FormattedSurveyAnswers = {
  howOften?: string;
  howOftenDetails?: string;
  trainReasons?: string[];
  trainReasonsDetails?: string;
  frameworks?: string[];
  frameworksDetails?: string;
  broughtBy?: string[];
  broughtByDetails?: string;
  teamSize?: string;
  teamSizeDetails?: string;
  learningFrom?: string[];
  learningFromDetails?: string;
  whichTools?: string[];
  whichToolsOther?: string;
  mlUseCases?: string[];
  mlUseCasesOther?: string;
  wandbDiscoveryOpts?: string[];
  wandbDiscoveryOptsOther?: string;
};

export type ViewerTraits = FormattedSurveyAnswers & {
  email: string;
  username?: string;
  name: string;
  createdAt?: string;
  website?: string;
  description?: string;
  organization: string;
  company: {id: string; name: string; website: null} | null;
  teamCount?: number;
  team?: string;
  teams?: string;
  isPaid?: boolean;
};

/** A team the shell reports as a Segment group. */
export type ViewerGroup = {
  name: string;
  entityName: string;
  isPaid: boolean | null;
};

function onboardingAnswersToArray(
  response?: OnboardingChoices
): string[] | undefined {
  if (response == null) {
    return undefined;
  }
  return Object.keys(response).filter(key => response[key]);
}

function formatSurveyAnswers(
  userInfo: ViewerUserInfo | undefined
): FormattedSurveyAnswers {
  if (userInfo == null) {
    return {};
  }
  return {
    howOften: userInfo.howOften,
    howOftenDetails: userInfo.howOftenDetails,
    trainReasons: onboardingAnswersToArray(userInfo.trainReasons),
    trainReasonsDetails: userInfo.trainReasonsDetails,
    frameworks: onboardingAnswersToArray(userInfo.frameworks),
    frameworksDetails: userInfo.frameworksDetails,
    broughtBy: onboardingAnswersToArray(userInfo.broughtBy),
    broughtByDetails: userInfo.broughtByDetails,
    teamSize: userInfo.teamSize,
    teamSizeDetails: userInfo.teamSizeDetails,
    learningFrom: onboardingAnswersToArray(userInfo.learningFrom),
    learningFromDetails: userInfo.learningFromDetails,
    whichTools: onboardingAnswersToArray(userInfo.whichTools),
    whichToolsOther: userInfo.whichToolsOther,
    mlUseCases: onboardingAnswersToArray(userInfo.mlUseCases),
    mlUseCasesOther: userInfo.mlUseCasesOther,
    wandbDiscoveryOpts: onboardingAnswersToArray(userInfo.wandbDiscoveryOpts),
    wandbDiscoveryOptsOther: userInfo.wandbDiscoveryOptsOther,
  };
}

function teamNodes(viewer: ViewerAnalytics_UserFragment) {
  return (viewer.teams?.edges ?? [])
    .map(edge => edge.node)
    .filter((node): node is NonNullable<typeof node> => node != null);
}

/**
 * Traits for `identify`. Returns null when the viewer has no email, matching
 * the original's guard: an emailless viewer is an incomplete session, not a
 * user worth identifying.
 */
export function viewerTraits(
  viewer: ViewerAnalytics_UserFragment
): ViewerTraits | null {
  if (!viewer.email) {
    return null;
  }

  const userInfo = (viewer.userInfo ?? undefined) as ViewerUserInfo | undefined;
  const [firstOrg] = viewer.organizations;
  const teams = teamNodes(viewer);

  return {
    email: viewer.email,
    username: viewer.username ?? undefined,
    name: viewer.name,
    createdAt:
      typeof viewer.createdAt === 'string' ? viewer.createdAt : undefined,
    website: userInfo?.websiteUrl,
    description: userInfo?.bio,
    organization: firstOrg?.name ?? '',
    // The id is stable and preserves analytics history if the org is renamed.
    company: firstOrg
      ? {id: firstOrg.id, name: firstOrg.name, website: null}
      : null,
    ...formatSurveyAnswers(userInfo),
    ...(teams.length > 0 && {
      teamCount: teams.length,
      team: viewer.entity ?? undefined,
      // Segment will not ingest a list as a trait, so this stays a joined string.
      teams: teams.map(team => team.name).join(', '),
      isPaid: teams.some(team => team.isPaid === true),
    }),
  };
}

/** One Segment group per team the viewer belongs to. Personal entities are not teams. */
export function viewerGroups(
  viewer: ViewerAnalytics_UserFragment
): ViewerGroup[] {
  return teamNodes(viewer)
    .filter(team => team.isTeam)
    .map(team => ({
      name: team.name,
      entityName: team.name,
      isPaid: team.isPaid,
    }));
}
